import CryptoJS from "crypto-js";

/**
 * The function encrypts data using AES encryption with a secret key and returns the encoded result.
 * @param data - The data that needs to be encrypted. It can be of any data type, but it will be
 * converted to a JSON string before encryption.
 * @param secretKey - The secretKey parameter is an optional parameter that represents the secret key
 * used to encrypt the data. If a secret key is not provided, the default key will be used.
 * @returns The function `encryptData` is returning an encoded string that is the result of encrypting
 * the input `data` using the Advanced Encryption Standard (AES) algorithm with a secret key.
 */
export const encrypt = (data, secretKey) => {
  const secret = secretKey || process.env.REACT_APP_ENCRYPTION_KEY;

  return CryptoJS.AES.encrypt(data.toString(), secret).toString();
};

/**
 * The function decrypts encrypted data using a secret key and returns the decrypted data.
 * @param encryptedData - The encrypted data that needs to be decrypted.
 * @param secretKey - The secret key is a string that is used to encrypt and decrypt data using the AES
 * encryption algorithm. If a secret key is not provided, then default key will be used.
 * @returns The `decrypt` function returns the decrypted data after decrypting the `encryptedData`
 * using the `secretKey`.
 */
export const decrypt = (encryptedData, secretKey) => {
  const secret = secretKey || process.env.REACT_APP_ENCRYPTION_KEY;
  // console.log("encryptedData:", encryptedData);
  // console.log("secret:", secret);
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, secret);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    // console.log("decryptedData: ", decryptedData);

    return decryptedData;
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

/** Enum, to select framework : Django, Express, PHP or React */
export const WebFramework = {
  DJANGO: "Django",
  EXPRESS: "Express",
  PHP: "PHP",
  REACT: "React",
};

/** Get domain url based on web framework.
 * By default webframework is Django. `(webFramework = WebFramework.DJANGO)`
 */
export const getDomain = (webFramework = WebFramework.DJANGO) => {
  if (window.location.hostname.trim() === "localhost") {
    if (webFramework === WebFramework.DJANGO)
      return process.env.REACT_APP_LOCAL_DJANGO;
    else if (webFramework === WebFramework.EXPRESS)
      return process.env.REACT_APP_LOCAL_EXPRESS;
    else if (webFramework === WebFramework.REACT)
      return process.env.REACT_APP_LOCAL_FRONT_END;
    else if (webFramework === WebFramework.PHP)
      return process.env.REACT_APP_LOCAL_GO_DIGIT_DOMAIN;
    else throw new Error("Invalid web framework, not available..");
  } else if (window.location.hostname.trim() === "it.acuvisor.com") {
    if (webFramework === WebFramework.DJANGO)
      return process.env.REACT_APP_PRODUCTION_DJANGO;
    else if (webFramework === WebFramework.EXPRESS)
      return process.env.REACT_APP_PRODUCTION_EXPRESS;
    else if (webFramework === WebFramework.REACT)
      return process.env.REACT_APP_PRODUCTION_FRONT_END;
    else if (webFramework === WebFramework.PHP)
      return process.env.REACT_APP_PRODUCTION_GO_DIGIT_DOMAIN;
    else throw new Error("Invalid web framework, not available..");
  } else {
    if (webFramework === WebFramework.DJANGO)
      return process.env.REACT_APP_UAT_DJANGO;
    else if (webFramework === WebFramework.EXPRESS)
      return process.env.REACT_APP_UAT_EXPRESS;
    else if (webFramework === WebFramework.REACT)
      return process.env.REACT_APP_UAT_FRONT_END;
    else throw new Error("Invalid web framework, not available for php");
  }
};

/**
 * The function downloads a PDF file with the given name and content in base64 format.
 * @param pdfBytes - a string of base64-encoded PDF data that represents the content of the PDF file to
 * be downloaded.
 * @param [fileName=file1] - The name of the file that will be downloaded. If no name is provided, the
 * default name "file1" will be used.
 */
export const downloadPDF = (pdfBytes, fileName = "file1") => {
  const linkSource = `data:application/pdf;base64,${pdfBytes}`;
  const downloadLink = document.createElement("a");

  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};
